import { useMemo } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { useGtfsClient } from './useGtfsClient';
import { DateTime } from 'luxon';
import {
  ApiRouteStops,
  ApiRouteVariant,
  ApiStopLinkShape,
  ApiTablePeriod,
  RouteVariant,
} from '../types/gtfs';
import simplify from '../helpers/simplify';

const collator = new Intl.Collator('sv-SE');

interface GtfsData {
  routeVariantsByRouteID: Record<string, RouteVariant>;
}

export const useGtfs = (enabled = true): UseQueryResult<GtfsData, Error> => {
  const client = useGtfsClient();

  return useQuery<GtfsData, Error>(
    ['gtfsData'],
    async () => {
      if (!client) {
        throw new Error('GTFS client not available');
      }

      const tablePeriods = await client.tablePeriods();

      const tablePeriodID = getCurrentTablePeriod(tablePeriods)?.id;
      if (!tablePeriodID) {
        throw new Error('No current table period');
      }

      const fetchResults = await Promise.all([
        client.routes(tablePeriodID),
        client.routeStops(tablePeriodID),
        client.stopLinkShapes(tablePeriodID),
      ]);

      const routes = fetchResults[0];
      const routeStops = fetchResults[1];
      const stopLinkShapes = fetchResults[2];

      const routesByRouteID = groupRoutesByRouteID(routes);

      const routeStopsByRouteID = groupRouteStopsByRouteID(routeStops);

      const pathByStopLinkID = createPathByStopLinkID(stopLinkShapes);

      const stopLinkIdsByRouteID =
        createStopLinkIdsByRouteID(routeStopsByRouteID);

      const routeVariantsByRouteID = createRouteVariantsByRouteID(
        stopLinkIdsByRouteID,
        pathByStopLinkID,
        routesByRouteID,
      );

      return {
        routeVariantsByRouteID,
      };
    },
    {
      enabled: !!client && enabled,
      staleTime: 1000 * 60 * 60, // 1 hour
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  );
};

const createStopLinkIdsByRouteID = (
  routeStopsByRouteID: Record<string, ApiRouteStops[]>,
): Record<string, string[]> => {
  return Object.keys(routeStopsByRouteID).reduce<Record<string, string[]>>(
    (acc, routeID) => {
      const stops = Array.from(new Set(routeStopsByRouteID[routeID]));
      const stopLinkIds = stops.reduce<string[]>(
        (stopLinkIds, stop, i, arr) => {
          if (i === arr.length - 1) {
            return stopLinkIds;
          }
          if (stop.stop_id === arr[i + 1].stop_id) {
            return stopLinkIds;
          }
          stopLinkIds.push(`${stop.stop_id}:${arr[i + 1].stop_id}`);
          return stopLinkIds;
        },
        [],
      );
      acc[routeID] = stopLinkIds;
      return acc;
    },
    {},
  );
};

const createRouteVariantsByRouteID = (
  stopLinkIdsByRouteID: Record<string, string[]>,
  pathByStopLinkID: Record<string, [number, number][]>,
  routesByRouteID: Record<string, ApiRouteVariant>,
): Record<string, RouteVariant> => {
  return Object.keys(stopLinkIdsByRouteID).reduce<Record<string, RouteVariant>>(
    (acc, routeID) => {
      const stopLinkIds = stopLinkIdsByRouteID[routeID];
      const route = routesByRouteID[routeID];
      if (!route) {
        return acc;
      }
      const rawPath = stopLinkIds
        .map((linkId, i) =>
          i === 0
            ? pathByStopLinkID[linkId]
            : pathByStopLinkID[linkId]?.slice(1),
        )
        .filter(Boolean)
        .flat();

      if (rawPath.length === 0) {
        return acc;
      }

      acc[routeID] = {
        route_id: routeID,
        direction_id: route.direction_id,
        name: route.route_alias || route.route_short_name,
        route_long_name: route.route_long_name,
        route_short_name: route.route_short_name,
        path: simplify(rawPath, 0.00005, true).map((coords) => ({
          lng: coords[0],
          lat: coords[1],
        })),
      };
      return acc;
    },
    {},
  );
};

const createPathByStopLinkID = (
  stopLinkShapes: ApiStopLinkShape[],
): Record<string, [number, number][]> => {
  return stopLinkShapes
    .sort((a, b) => a.shape_pt_sequence - b.shape_pt_sequence)
    .reduce<Record<string, [number, number][]>>((acc, shape) => {
      if (!acc[shape.shape_id]) {
        acc[shape.shape_id] = [];
      }
      acc[shape.shape_id].push([shape.shape_pt_lon, shape.shape_pt_lat]);
      return acc;
    }, {});
};

const groupRoutesByRouteID = (
  routes: ApiRouteVariant[],
): Record<string, ApiRouteVariant> => {
  return routes.reduce<Record<string, ApiRouteVariant>>((acc, route) => {
    acc[route.route_id] = route;
    return acc;
  }, {});
};

const groupRouteStopsByRouteID = (
  routeStops: ApiRouteStops[],
): Record<string, ApiRouteStops[]> => {
  return routeStops
    .sort((a, b) => a.stop_sequence - b.stop_sequence)
    .reduce<Record<string, ApiRouteStops[]>>((acc, routeStop) => {
      if (!acc[routeStop.route_id]) {
        acc[routeStop.route_id] = [];
      }
      acc[routeStop.route_id].push(routeStop);
      return acc;
    }, {});
};

/** Get the current table period */
const getCurrentTablePeriod = (
  tablePeriods: ApiTablePeriod[],
): ApiTablePeriod | undefined => {
  tablePeriods.sort((a, b) => {
    const startA = DateTime.fromISO(a.date_start);
    const startB = DateTime.fromISO(b.date_start);

    return startB.toMillis() - startA.toMillis();
  });

  return tablePeriods.find((period) => {
    const now = DateTime.now();
    const start = DateTime.fromISO(period.date_start);
    const end = period.date_end ? DateTime.fromISO(period.date_end) : undefined;

    if (!end) {
      return now >= start;
    }

    return now >= start && now <= end;
  });
};

const createRouteNameLookup = (
  routeVariantsByRouteID: Record<string, RouteVariant> | undefined,
): Record<string, string> => {
  const lookup: Record<string, string> = {};
  if (!routeVariantsByRouteID) {
    return lookup;
  }

  Object.values(routeVariantsByRouteID).forEach((variant) => {
    const name = variant.name?.trim();
    const shortName = variant.route_short_name?.trim();
    if (!name || !shortName || name === shortName || lookup[shortName]) {
      return;
    }
    lookup[shortName] = name;
  });

  return lookup;
};

/**
 * Maps a passing route's short name to its GTFS route name, when resolvable
 * (e.g. `{ '461': 'Centrumlinjen' }`). Returns an empty lookup until the GTFS
 * data has loaded. `enabled` is forwarded to {@link useGtfs} so callers can
 * avoid fetching GTFS data when it isn't needed.
 */
export const useRouteNameLookup = (enabled = true): Record<string, string> => {
  const { data: gtfsData } = useGtfs(enabled);

  return useMemo(
    () => createRouteNameLookup(gtfsData?.routeVariantsByRouteID),
    [gtfsData],
  );
};

/** Sort routes by name (numeric first, then alphabetic) */
export const routeNameSortFn = (a: string, b: string): number => {
  const valueA = parseInt(a, 10);
  const valueB = parseInt(b, 10);

  if (isNaN(valueA) && isNaN(valueB)) {
    return collator.compare(a, b);
  } else if (isNaN(valueA)) {
    return 1;
  } else if (isNaN(valueB)) {
    return -1;
  }

  return valueA - valueB;
};
